import React from 'react';
import { Box, Typography } from '@material-ui/core';
import CurrencyInput from 'react-currency-input-field';

// Simple input component to replace sliders
const SimpleFinancialInput = ({ label, name, fieldName, object, onChange, prefix = '$', isNumber = false, min = 0, max }) => {
  const [isTouched, setIsTouched] = React.useState(false);
  const [showMinMessage, setShowMinMessage] = React.useState(false);
  
  // Show empty string if not touched and value is 0, otherwise show the actual value
  const currentValue = !isTouched && object[fieldName] === 0 ? '' : object[fieldName];

  const formatDisplayValue = (val) => {
    if (isNumber) {
      return val?.toLocaleString() || '0';
    }
    return val ? `$${val.toLocaleString()}` : '$0';
  };

  const handleFocus = () => {
    setIsTouched(true);
    setShowMinMessage(false);
  };

  const handleBlur = () => {
    const val = object[fieldName];
    // Only enforce min if the field is empty, null, undefined, or 0
    if (val === '' || val === null || val === undefined || val === 0) {
      // Set to min when field is empty on blur
      onChange({ target: { value: min, name: fieldName } });
      // Show message only if min is greater than 0 (like for Number of Relationships)
      if (min > 0) {
        setShowMinMessage(true);
        // Hide message after 3 seconds
        setTimeout(() => setShowMinMessage(false), 6000);
      }
    } else {
      let numericValue = parseFloat(val);
      if (isNaN(numericValue)) {
        numericValue = min;
      }
      if (min !== undefined && numericValue < min) numericValue = min;
      if (max !== undefined && numericValue > max) numericValue = max;
      onChange({ target: { value: numericValue, name: fieldName } });
    }
  };

  return (
    <Box style={{ marginBottom: '20px' }}>
      <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
        {label}
      </Typography>
      {showMinMessage && (
        <Box style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '8px 12px',
          marginBottom: '8px',
          fontSize: '13px',
          color: '#856404'
        }}>
          Field auto-filled with minimum value: {formatDisplayValue(min)}
        </Box>
      )}
      <CurrencyInput
        value={currentValue}
        onValueChange={(val) => {
          // Allow completely empty string during typing so user can delete everything
          if (val === undefined || val === '') {
            onChange({ target: { value: '', name: fieldName } });
          } else {
            let numericValue = parseFloat(val);
            if (max !== undefined) {
              numericValue = Math.min(numericValue, max);
            }
            onChange({ target: { value: numericValue, name: fieldName } });
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ 
          width: '100%', 
          padding: '12px 16px', 
          border: '1px solid #ccc', 
          borderRadius: '4px',
          fontSize: '16px'
        }}
        placeholder={isNumber ? "Enter amount" : "Enter amount"}
        prefix={isNumber ? "" : prefix}
        decimalsLimit={isNumber ? 0 : 2}
        allowNegativeValue={false}
        allowDecimals={!isNumber}
      />
      
      {/* Min/Max Display */}
      <Box style={{ 
        fontSize: '12px', 
        color: '#666', 
        marginTop: '4px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>Min: {formatDisplayValue(min)}</span>
        <span>Max: {formatDisplayValue(max)}</span>
      </Box>
    </Box>
  );
};

const FinancialDetails = ({ formData, handleChange, user, company }) => {
  const partialSalesPropertyCasualty = company.feature_types.includes('partial_sales_property_casualty');
  
  // Get max values from company with defaults
  const maxClients = company.max_partial_sale_clients || 500;
  const maxAum = company.max_partial_sale_aum || 500000000;
  const maxFeeBasedRevenue = company.max_partial_sale_fee_based_revenue || 500000;
  const maxTrailsRevenue = company.max_partial_sale_trails_revenue || 500000;
  const maxDirectRevenue = company.max_partial_sale_direct_revenue || 500000;
  const maxCommissionRevenue = company.max_partial_sale_commission_revenue || 500000;
  
  return (
    <div>
      <Box>
        <Typography variant="h6" gutterBottom>
          Financial Details
        </Typography>
        
        <SimpleFinancialInput
          label="Number Of Relationships"
          name="numberOfClients"
          object={formData}
          fieldName="numberOfClients"
          onChange={handleChange}
          prefix=''
          isNumber={true}
          min={1}
          max={Math.min(user?.number_of_clients || maxClients, maxClients)}
        />
        
        <SimpleFinancialInput
          label={partialSalesPropertyCasualty ? "Total Premium" : "Assets Under Management"}
          name="assetsUnderManagement"
          object={formData}
          fieldName="assetsUnderManagement"
          onChange={handleChange}
          min={0}
          max={Math.min(user?.assets_under_management || maxAum, maxAum)}
        />
        
        <SimpleFinancialInput
          label={partialSalesPropertyCasualty ? "Recurring Commission Revenue" : "Fee Based Revenue"}
          name="feeBasedRevenue"
          object={formData}
          fieldName="feeBasedRevenue"
          onChange={handleChange}
          min={0}
          max={Math.min(
            partialSalesPropertyCasualty ? (user?.recurring_revenue || maxFeeBasedRevenue) : (user?.fee_based_revenue || maxFeeBasedRevenue), 
            maxFeeBasedRevenue
          )}
        />
        
        {!partialSalesPropertyCasualty && (
          <SimpleFinancialInput
            label="Trails Revenue"
            name="trailerRevenue"
            object={formData}
            fieldName="trailerRevenue"
            onChange={handleChange}
            min={0}
            max={Math.min(user?.trailer_revenue || maxTrailsRevenue, maxTrailsRevenue)}
          />
        )}
        
        {!partialSalesPropertyCasualty && (company && !company.feature_types.includes('hide_direct_revenue')) && (
          <SimpleFinancialInput
            label="Direct Revenue"
            name="directRevenue"
            object={formData}
            fieldName="directRevenue"
            onChange={handleChange}
            min={0}
            max={Math.min(user?.direct_revenue || maxDirectRevenue, maxDirectRevenue)}
          />
        )}
        
        <SimpleFinancialInput
          label={partialSalesPropertyCasualty ? "New Commission Revenue" : "Commission Revenue"}
          name="commissionRevenue"
          object={formData}
          fieldName="commissionRevenue"
          onChange={handleChange}
          min={0}
          max={Math.min(
            partialSalesPropertyCasualty ? 
              Math.max((user?.annual_revenue || 0) - (user?.recurring_revenue || 0), maxCommissionRevenue) : 
              (user?.transactional_revenue || maxCommissionRevenue),
            maxCommissionRevenue
          )}
        />
      </Box>
    </div>
  );
};

export default FinancialDetails;